import type { AgentHost } from '@runtime/agent.js';
import { detectOem, type OemDetectionProbe } from '@runtime/oem/detect.js';
import type { DeviceExtensionModule, OemId, OemModuleLoader } from '@runtime/oem/types.js';

/**
 * Build an OEM detection probe from the agent host: system properties via
 * the host's `getSystemProperty` (when available) plus the WebView's
 * User-Agent as a fallback signal.
 */
export function probeFromAgentHost(host: AgentHost): OemDetectionProbe {
  const probe: OemDetectionProbe = {
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  };
  const getSystemProperty = host.getSystemProperty;
  if (getSystemProperty !== undefined) {
    probe.getSystemProperty = (name) => getSystemProperty.call(host, name);
  }
  return probe;
}

/**
 * OEM loader with real, SDK-free auto-detection. Replaces the always-false
 * stub `detect()`: each module's `detect()` runs {@link detectOem} and
 * activates only on the matching vendor. `apply`/`dispose` stay no-ops —
 * the per-OEM SDK integrations (Zebra MX, Samsung Knox, Honeywell
 * EZConfig) aren't shipped yet, so we detect the hardware and surface it
 * in diagnostics without claiming an SDK-level integration we don't have.
 *
 * Forced detection (`device.detection = 'forceZebra'` etc.) bypasses
 * `detect()` in the orchestrator and still works unchanged.
 */
export function createDetectingOemLoader(probe: OemDetectionProbe): OemModuleLoader {
  return (oem: OemId): Promise<DeviceExtensionModule | null> =>
    Promise.resolve({
      oem,
      detect: async () => (await detectOem(probe)) === oem,
      apply: () => Promise.resolve(),
      dispose: () => Promise.resolve(),
    });
}
